import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Sparkles, BookOpen, ArrowRight, MessageCircle, Gamepad2, ClipboardCheck, Sun, CheckCircle2, Shield, PartyPopper, X, Clock } from "lucide-react";
import api, { trackClick, tutoringUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import WelcomeTour from "../components/WelcomeTour";
import WeeklyFocus from "../components/WeeklyFocus";
import StreakCalendar from "../components/StreakCalendar";

// Deterministic A/B assignment: even user-id last-char -> "top", odd -> "bottom"
function pickVariant(userId) {
    if (!userId) return "bottom";
    const last = userId.slice(-1).toLowerCase();
    const code = last.charCodeAt(0);
    return code % 2 === 0 ? "top" : "bottom";
}

export default function Dashboard() {
    const { user } = useAuth();
    const [pillars, setPillars] = useState([]);
    const [progress, setProgress] = useState({ xp: 0, streak: 0, streak_freezes: 0, pending_freeze_notice: false, completed_lessons: [], active_dates: [] });
    const [path, setPath] = useState(null);
    const [sub, setSub] = useState(null);
    const tutoringVariant = pickVariant(user?.id);
    const TutoringCard = () => (
        <section className="card-rw p-6 md:p-7 flex flex-wrap items-center gap-5" style={{ borderLeft: "6px solid #D47A55" }} data-testid="dashboard-virtual-tutoring" data-variant={tutoringVariant}>
            <div className="w-11 h-11 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D47A55" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div className="flex-1 min-w-[240px]">
                <p className="label-eyebrow mb-1" style={{ color: "#D47A55" }}>Want a real teacher too?</p>
                <p className="text-[#1C2321]">
                    Add live weekly tutoring from <strong>Lift Up Literacy LLC</strong> for a coach on your shoulder alongside the app.
                </p>
            </div>
            <a
                href={tutoringUrl("dashboard")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("dashboard", "liftupliteracy.com", tutoringVariant)}
                data-testid="dashboard-virtual-tutoring-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#D47A55] text-white font-medium hover:bg-[#B86644] transition-colors"
            >
                Explore tutoring
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>
            </a>
        </section>
    );

    useEffect(() => {
        // Fetch subscription first — if trial is over, DON'T fire the gated
        // content calls (they'd 402 and the api.js interceptor would bounce
        // us to /pricing before we can show the trial-ended banner).
        api.get("/subscription/me").then((r) => {
            setSub(r.data);
            if (r.data.active) {
                api.get("/curriculum").then((res) => setPillars(res.data)).catch(() => {});
                api.get("/path/today").then((res) => setPath(res.data)).catch(() => {});
            }
        }).catch(() => {});
        api.get("/progress/me").then((r) => setProgress(r.data));
    }, []);

    const dismissFreeze = async () => {
        setProgress((p) => ({ ...p, pending_freeze_notice: false }));
        try { await api.post("/progress/dismiss-freeze-notice"); } catch {}
    };

    const assessment = user?.assessment_result;
    const tookAssessment = !!assessment;

    return (
        <main className="container-page py-12 fade-up">
            <WelcomeTour />
            <p className="label-eyebrow mb-2" data-testid="dashboard-greeting">Welcome back</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-10" style={{ fontFamily: "Outfit" }} data-testid="dashboard-title">
                Good to see you, {user?.name?.split(" ")[0] || "friend"}.
            </h1>

            {/* Trial / subscription banners — hidden in FREE_MODE. */}
            {sub && !sub.free_mode && sub.status === "trialing" && sub.active && (
                <div
                    className="card-rw p-5 mb-6 flex flex-wrap items-center gap-4 fade-up"
                    style={{ borderLeft: "6px solid #D47A55", background: "#FEF6F0" }}
                    data-testid="trial-banner"
                >
                    <div className="w-10 h-10 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-[#D47A55]" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <p className="font-medium text-[#1C2321] mb-1">
                            {sub.days_left === 0 ? "Your trial ends today" : `${sub.days_left} ${sub.days_left === 1 ? "day" : "days"} left in your free trial`}
                        </p>
                        <p className="text-[#515B57]">Full access to lessons, games, and the tutor. Subscribe anytime to keep going after your trial.</p>
                    </div>
                    <Link
                        to="/pricing"
                        data-testid="trial-banner-cta"
                        className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                    >
                        See plans
                    </Link>
                </div>
            )}
            {sub && !sub.free_mode && !sub.active && sub.status !== "active" && (
                <div
                    className="card-rw p-5 mb-6 flex flex-wrap items-center gap-4 fade-up"
                    style={{ borderLeft: "6px solid #7D2F2F", background: "#FDF3F3" }}
                    data-testid="trial-ended-banner"
                >
                    <div className="w-10 h-10 rounded-lg bg-[#F8E6E6] flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-[#7D2F2F]" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <p className="font-medium text-[#1C2321] mb-1">Your trial has ended</p>
                        <p className="text-[#515B57]">Your progress is safe. Subscribe to unlock lessons, games, and the tutor again.</p>
                    </div>
                    <Link
                        to="/pricing"
                        data-testid="trial-ended-cta"
                        className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                    >
                        Subscribe
                    </Link>
                </div>
            )}

            {/* Streak-save used banner */}
            {progress.pending_freeze_notice && (
                <div
                    className="card-rw p-5 mb-6 flex items-start gap-4 fade-up"
                    style={{ borderLeft: "6px solid #2D5D4E", background: "#F2F7F4" }}
                    data-testid="freeze-used-banner"
                >
                    <div className="w-10 h-10 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1">
                        <p className="font-medium text-[#1C2321] mb-1">Streak save used — your streak stayed alive.</p>
                        <p className="text-[#515B57]">
                            You missed a day, and we used one of your saves so you wouldn't lose your momentum.
                            You have <span className="font-medium text-[#1C2321]">{progress.streak_freezes}</span> {progress.streak_freezes === 1 ? "save" : "saves"} left — you earn a new one every 7 days you show up.
                        </p>
                    </div>
                    <button
                        onClick={dismissFreeze}
                        className="text-[#819888] hover:text-[#1C2321] p-1"
                        aria-label="Dismiss"
                        data-testid="freeze-banner-dismiss"
                    >
                        <X className="w-5 h-5" strokeWidth={1.8} />
                    </button>
                </div>
            )}

            <WeeklyFocus />

            {/* Placement CTA — top priority for new users */}
            {!tookAssessment ? (
                <Link
                    to="/app/assessment"
                    data-testid="dashboard-take-assessment"
                    onClick={() => trackClick("dashboard", "internal:take-assessment")}
                    className="card-rw block p-7 md:p-9 mb-10 hover:-translate-y-0.5"
                    style={{ borderLeft: "6px solid #2D5D4E" }}
                >
                    <div className="flex items-start gap-5">
                        <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                            <ClipboardCheck className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                        </div>
                        <div className="flex-1">
                            <p className="label-eyebrow mb-1">Start here · 5 minutes</p>
                            <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Take the placement check</h2>
                            <p className="text-[#515B57] text-lg leading-relaxed">
                                Sixteen short questions tell us exactly where to begin — so you don't waste a minute on what you already know.
                                Every prompt can be read aloud to you.
                            </p>
                        </div>
                        <ArrowRight className="w-6 h-6 text-[#2D5D4E] mt-2 hidden md:block" />
                    </div>
                </Link>
            ) : (
                <div className="card-rw p-6 mb-10 flex flex-wrap items-center gap-5" style={{ borderLeft: "6px solid #2D5D4E" }} data-testid="dashboard-assessment-result">
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <p className="label-eyebrow mb-1">Your placement · {assessment.level_label}</p>
                        <p className="text-lg text-[#1C2321]">
                            Recommended start: <span className="font-medium">{assessment.recommended_pillar_title}</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/app/assessment"
                            data-testid="dashboard-retake-assessment"
                            onClick={() => trackClick("dashboard", "internal:retake-assessment")}
                            className="px-4 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#515B57] font-medium hover:bg-[#E8E2D2] hover:text-[#1C2321]"
                        >
                            Retake
                        </Link>
                        <Link
                            to={`/app/curriculum/${assessment.recommended_pillar_id}/lesson/${assessment.recommended_lesson_id}`}
                            data-testid="dashboard-resume-recommended"
                            className="px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                        >
                            Continue
                        </Link>
                    </div>
                </div>
            )}

            {/* Virtual tutoring cross-sell (A/B: variant='top' renders here) */}
            {tutoringVariant === "top" && <div className="mb-10"><TutoringCard /></div>}

            {/* Today's 5-minute path */}
            {path && (
                <section className="mb-12 fade-up" data-testid="daily-path">
                    {path.both_done_today ? (
                        <div className="card-rw p-8 md:p-10 text-center" style={{ borderTop: "6px solid #3A7D5C" }} data-testid="daily-path-complete">
                            <PartyPopper className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                            <p className="label-eyebrow mb-2" style={{ color: "#3A7D5C" }}>Today's path complete</p>
                            <h2 className="text-3xl font-medium text-[#1C2321] mb-2" style={{ fontFamily: "Outfit" }}>
                                That's a day well spent.
                            </h2>
                            <p className="text-lg text-[#515B57] max-w-xl mx-auto mb-6">
                                You finished today's lesson and locked it in with practice. Come back tomorrow for a fresh path —
                                or keep going below.
                            </p>
                            <div className="flex justify-center flex-wrap gap-3">
                                <Link
                                    to="/app/curriculum"
                                    data-testid="daily-path-more-lessons"
                                    className="px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                                >
                                    Explore more lessons
                                </Link>
                                <Link
                                    to="/app/games"
                                    data-testid="daily-path-more-games"
                                    className="px-6 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2]"
                                >
                                    Play another game
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <p className="label-eyebrow mb-1 flex items-center gap-2">
                                        <Sun className="w-3.5 h-3.5" /> Today · 5 minutes
                                    </p>
                                    <h2 className="text-2xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                                        Your path for today
                                    </h2>
                                </div>
                            </div>
                            <p className="text-[#515B57] mb-5 max-w-2xl">{path.intro}</p>
                            <div className="grid md:grid-cols-2 gap-5">
                                <Link
                                    to={path.lesson.url}
                                    data-testid="daily-path-lesson"
                                    className="card-rw p-7 hover:-translate-y-0.5 flex flex-col relative"
                                    style={{ borderLeft: "6px solid #2D5D4E" }}
                                >
                                    {path.lesson.done_today && (
                                        <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-xs font-medium text-[#3A7D5C] bg-[#E8F1EA] px-2 py-1 rounded">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Done today
                                        </span>
                                    )}
                                    <div className="flex items-center gap-2 mb-3">
                                        <BookOpen className="w-5 h-5 text-[#2D5D4E]" strokeWidth={1.7} />
                                        <p className="label-eyebrow">Step 1 · Lesson</p>
                                    </div>
                                    <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>{path.lesson.title}</h3>
                                    <p className="text-[#515B57] mb-4 flex-1">{path.lesson.objective}</p>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-[#819888]">{path.pillar.title} · +{path.lesson.xp} XP</p>
                                        <ArrowRight className="w-5 h-5 text-[#2D5D4E]" />
                                    </div>
                                </Link>
                                <Link
                                    to={path.game.url}
                                    data-testid="daily-path-game"
                                    className="card-rw p-7 hover:-translate-y-0.5 flex flex-col relative"
                                    style={{ borderLeft: "6px solid #D47A55" }}
                                >
                                    {path.game.done_today && (
                                        <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-xs font-medium text-[#3A7D5C] bg-[#E8F1EA] px-2 py-1 rounded">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Done today
                                        </span>
                                    )}
                                    <div className="flex items-center gap-2 mb-3">
                                        <Gamepad2 className="w-5 h-5 text-[#D47A55]" strokeWidth={1.7} />
                                        <p className="label-eyebrow">Step 2 · Reinforce</p>
                                    </div>
                                    <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>{path.game.title}</h3>
                                    <p className="text-[#515B57] mb-4 flex-1">{path.game.desc}</p>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-[#819888]">Earn XP per round</p>
                                        <ArrowRight className="w-5 h-5 text-[#D47A55]" />
                                    </div>
                                </Link>
                            </div>
                        </>
                    )}
                </section>
            )}

            <div className="grid md:grid-cols-3 gap-6 mb-12 stagger">
                <div className="card-rw p-6 flex items-center gap-4 relative" data-testid="stat-streak">
                    {progress.streak_freezes > 0 && (
                        <span
                            className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-medium text-[#1F4237] bg-[#E0EDE6] px-2 py-1 rounded"
                            title={`${progress.streak_freezes} streak ${progress.streak_freezes === 1 ? "save" : "saves"} available — auto-uses if you miss a day`}
                            data-testid="streak-freeze-badge"
                        >
                            <Shield className="w-3 h-3" strokeWidth={2} /> {progress.streak_freezes}
                        </span>
                    )}
                    <div className="w-12 h-12 rounded-lg bg-[#FDE9DD] flex items-center justify-center">
                        <Flame className="w-6 h-6 text-[#D47A55]" strokeWidth={1.6} />
                    </div>
                    <div>
                        <p className="label-eyebrow">Day streak</p>
                        <p className="text-3xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{progress.streak || 0}</p>
                    </div>
                </div>
                <div className="card-rw p-6 flex items-center gap-4" data-testid="stat-xp">
                    <div className="w-12 h-12 rounded-lg bg-[#E0EDE6] flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <div>
                        <p className="label-eyebrow">Experience</p>
                        <p className="text-3xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{progress.xp || 0}</p>
                    </div>
                </div>
                <div className="card-rw p-6 flex items-center gap-4" data-testid="stat-lessons">
                    <div className="w-12 h-12 rounded-lg bg-[#E8E2D2] flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-[#1F4237]" strokeWidth={1.6} />
                    </div>
                    <div>
                        <p className="label-eyebrow">Lessons done</p>
                        <p className="text-3xl font-semibold text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{progress.completed_lessons.length}</p>
                    </div>
                </div>
            </div>

            <StreakCalendar activeDates={progress.active_dates} streak={progress.streak || 0} />

            <section className="mb-14">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <p className="label-eyebrow mb-1">Your path</p>
                        <h2 className="text-2xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>The five pillars</h2>
                    </div>
                    <Link to="/app/curriculum" className="text-[#2D5D4E] hover:underline font-medium flex items-center gap-1" data-testid="dashboard-see-all-lessons">
                        See all <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
                    {pillars.map((p) => (
                        <Link
                            key={p.id}
                            to={`/app/curriculum/${p.id}`}
                            className="card-rw p-6 hover:-translate-y-1"
                            data-testid={`pillar-card-${p.id}`}
                        >
                            <p className="label-eyebrow mb-3">Step {p.order}</p>
                            <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>{p.title}</h3>
                            <p className="text-[#515B57] text-base leading-relaxed mb-4">{p.subtitle}</p>
                            <p className="text-sm text-[#819888]">{p.lesson_count} lessons</p>
                        </Link>                    ))}
                </div>
            </section>

            <section className="grid md:grid-cols-2 gap-6">
                <Link to="/app/games" className="card-rw p-8 hover:-translate-y-1" data-testid="dashboard-cta-games">
                    <Gamepad2 className="w-7 h-7 text-[#D47A55] mb-4" strokeWidth={1.6} />
                    <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Play a quick game</h3>
                    <p className="text-[#515B57]">Six short games to make today's lesson stick.</p>
                </Link>
                <Link to="/app/tutor" className="card-rw p-8 hover:-translate-y-1" data-testid="dashboard-cta-tutor">
                    <MessageCircle className="w-7 h-7 text-[#2D5D4E] mb-4" strokeWidth={1.6} />
                    <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Ask the tutor</h3>
                    <p className="text-[#515B57]">Stuck on a sound or a word? Ask anything, no judgment.</p>
                </Link>
            </section>

            {/* Virtual tutoring cross-sell — subtle, at the bottom */}
            {tutoringVariant === "bottom" && <div className="mt-10"><TutoringCard /></div>}
        </main>
    );
}
